import { useEffect, useMemo, useRef, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminAiGuide.css";

export type AdminGuideNavItem = {
  routeKey:string;
  path:string;
  label:string;
  description?:string|null;
};

type GuideRoute = {path:string;label:string;reason:string};
type GuideResult = {
  mode?:"AI"|"LOCAL_SEARCH";
  configured?:boolean;
  model?:string|null;
  answer?:string;
  routes?:GuideRoute[];
  followUps?:string[];
  warnings?:string[];
  message?:string;
};

const QUICK = [
  "배지 발급 대기는 어디서 확인해?",
  "심리검사를 새로 만들려면 어디로 가?",
  "학교별 운영 준비상태를 보고 싶어",
  "사용자 권한을 변경하려면?",
  "오류와 작업 이력을 추적하고 싶어"
];

function localMatches(query:string,navigation:AdminGuideNavItem[]){
  const needle=query.trim().toLowerCase();
  if(!needle)return [];
  return navigation.map((item)=>{
    const hay=[item.label,item.description,item.routeKey].filter(Boolean).join(" ").toLowerCase();
    const parts=needle.split(/\s+/).filter((part)=>part.length>=2);
    const score=(hay.includes(needle)?5:0)+parts.reduce((sum,part)=>sum+(hay.includes(part)?1:0),0);
    return {item,score};
  }).filter((row)=>row.score>0).sort((a,b)=>b.score-a.score).slice(0,5).map((row)=>row.item);
}

export function AdminAiGuide({
  client,
  open,
  currentPath,
  rootOrganizationId,
  navigation,
  onClose,
  onNavigate
}:{
  client:PlatformClient;
  open:boolean;
  currentPath:string;
  rootOrganizationId?:string|null;
  navigation:AdminGuideNavItem[];
  onClose:()=>void;
  onNavigate:(path:string)=>void;
}){
  const [query,setQuery]=useState("");
  const [result,setResult]=useState<GuideResult|null>(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [configured,setConfigured]=useState<boolean|null>(null);
  const [model,setModel]=useState("");
  const inputRef=useRef<HTMLInputElement|null>(null);

  useEffect(()=>{
    if(!open)return;
    inputRef.current?.focus();
    const onKey=(event:KeyboardEvent)=>{if(event.key==="Escape")onClose();};
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[open,onClose]);

  useEffect(()=>{
    let active=true;
    client.functions.invoke("admin-ai-guide",{body:{action:"STATUS"}}).then(({data,error})=>{
      if(!active)return;
      if(error){setConfigured(false);return;}
      const next=(data||{}) as GuideResult;
      setConfigured(Boolean(next.configured));
      setModel(typeof next.model==="string"?next.model:"");
    }).catch(()=>{if(active)setConfigured(false);});
    return()=>{active=false;};
  },[client]);

  const instant=useMemo(()=>localMatches(query,navigation),[query,navigation]);

  async function search(nextQuery=query){
    const value=nextQuery.trim();
    if(!value)return;
    setQuery(value);setBusy(true);setError("");setResult(null);
    const {data,error}=await client.functions.invoke("admin-ai-guide",{
      body:{
        action:"SEARCH",
        query:value,
        currentPath,
        rootOrganizationId:rootOrganizationId||null,
        navigation
      }
    });
    setBusy(false);
    if(error){setError("Admin 안내 검색을 완료하지 못했습니다.");return;}
    const next=(data||{}) as GuideResult;
    setResult(next);
    if(typeof next.configured==="boolean")setConfigured(next.configured);
    if(typeof next.model==="string")setModel(next.model);
  }

  function go(path:string){
    onNavigate(path);
    onClose();
  }

  if(!open)return null;

  return (
    <div className="admin-ai-layer" role="dialog" aria-modal="true" aria-label="Admin AI 운영 안내자">
      <button className="admin-ai-backdrop" aria-label="닫기" onClick={onClose}/>
      <aside className="admin-ai-panel">
        <header className="admin-ai-header">
          <div className="admin-ai-brand">
            <span className="admin-ai-mark">AI</span>
            <div><p className="eyebrow">ADMIN OPERATIONS GUIDE</p><h2>AI 운영 안내자</h2><p>기능·메뉴·운영상태를 자연어로 찾아드립니다.</p></div>
          </div>
          <button className="admin-ai-close" onClick={onClose}>×</button>
        </header>

        <div className="admin-ai-status">
          <span className={configured?"ready":""}>{configured===null?"AI 상태 확인 중":configured?"AI 검색 연결됨":"메뉴 검색 모드"}</span>
          {model&&<small>{model}</small>}
        </div>

        <form className="admin-ai-search" onSubmit={(event)=>{event.preventDefault();void search();}}>
          <span>⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(event)=>{setQuery(event.target.value);setResult(null);setError("");}}
            placeholder="예: 배지 100종은 어디서 관리하지?"
          />
          <button disabled={busy||!query.trim()}>{busy?"검색 중…":"검색"}</button>
        </form>

        {!query.trim() && !result && (
          <section className="admin-ai-quick">
            <p className="eyebrow">빠른 질문</p>
            <div>{QUICK.map((item)=><button key={item} onClick={()=>void search(item)}>{item}<span>→</span></button>)}</div>
          </section>
        )}

        {query.trim() && !result && !busy && instant.length>0 && (
          <section className="admin-ai-instant">
            <p className="eyebrow">메뉴 바로찾기</p>
            {instant.map((item)=>(
              <button key={item.path} onClick={()=>go(item.path)}>
                <span><strong>{item.label}</strong><small>{item.description||item.routeKey}</small></span><b>이동 →</b>
              </button>
            ))}
          </section>
        )}

        {busy && (
          <div className="admin-ai-thinking">
            <i/><div><strong>Admin을 검색하고 있습니다</strong><span>현재 메뉴와 접근 가능한 운영정보를 함께 확인합니다.</span></div>
          </div>
        )}

        {error&&<p className="error">{error}</p>}

        {result && (
          <div className="admin-ai-result">
            <div className="admin-ai-answer">
              <span className="admin-ai-mark small">AI</span>
              <p>{result.answer}</p>
            </div>

            {Array.isArray(result.routes)&&result.routes.length>0&&(
              <section className="admin-ai-routes">
                <p className="eyebrow">바로가기</p>
                {result.routes.map((route)=>(
                  <button key={route.path} onClick={()=>go(route.path)}>
                    <span><strong>{route.label}</strong><small>{route.reason}</small></span>
                    <b>열기 →</b>
                  </button>
                ))}
              </section>
            )}

            {Array.isArray(result.warnings)&&result.warnings.length>0&&(
              <div className="admin-ai-warnings">
                <strong>확인 필요</strong>
                {result.warnings.map((item,index)=><span key={index}>{item}</span>)}
              </div>
            )}

            {Array.isArray(result.followUps)&&result.followUps.length>0&&(
              <section className="admin-ai-followups">
                <p className="eyebrow">이어서 물어보기</p>
                <div>{result.followUps.map((item)=><button key={item} onClick={()=>void search(item)}>{item}</button>)}</div>
              </section>
            )}
          </div>
        )}

        <footer className="admin-ai-footer">
          <span>검색·안내 전용</span>
          <p>권한 변경, 발급 승인, 삭제 같은 관리 작업은 AI가 직접 실행하지 않습니다.</p>
        </footer>
      </aside>
    </div>
  );
}
